import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  TrendingUp, 
  TrendingDown, 
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  BarChart3,
  RefreshCw,
  ChevronRight,
  LineChart as LineChartIcon,
  PieChart,
  Briefcase
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  ComposedChart,
  Area,
  PieChart as RechartsPie,
  Pie,
  Cell
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value || 0);
};

const formatPercent = (value) => {
  if (value === undefined || value === null || isNaN(value)) return "0%";
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const VariationBadge = ({ value }) => {
  if (value === undefined || value === null || isNaN(value) || value === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[#86868B] text-xs">
        <Minus className="w-3 h-3" />
        0%
      </span>
    );
  }
  if (value > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[#34C759] text-xs font-medium">
        <ArrowUpRight className="w-3 h-3" />
        {formatPercent(value)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[#FF3B30] text-xs font-medium">
      <ArrowDownRight className="w-3 h-3" />
      {formatPercent(value)}
    </span>
  );
};

const YEAR_COLORS = {
  2023: "#9333ea", // purple
  2024: "#2563eb", // blue
  2025: "#16a34a", // green
  2026: "#ea580c", // orange
  2027: "#dc2626", // red
};

const getYearColor = (year) => YEAR_COLORS[year] || "#6b7280";

const Confronti = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState("revenue");
  const [selectedYear, setSelectedYear] = useState(null);
  const [viewMode, setViewMode] = useState("overview"); // overview, yearly, monthly

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/analytics/multi-year?start_year=2023`,
        getAuthHeaders()
      );
      setData(response.data);
      if (response.data.years?.length > 0) {
        setSelectedYear(response.data.years[response.data.years.length - 1]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const metricLabels = {
    revenue: "Fatturato",
    costs: "Costi Funerali",
    profit: "Utile",
    income: "Entrate Cassa",
    expenses: "Uscite Cassa",
    invoices_payable: "Fatture Passive",
    services_count: "Servizi Funerari",
    bank_income: "Entrate Banca",
    bank_expense: "Uscite Banca"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-[#86868B]">
        Nessun dato disponibile
      </div>
    );
  }

  const { years, yearly_summary, monthly_chart, monthly_tables } = data;

  return (
    <div className="space-y-6" data-testid="confronti-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1D1D1F]">Confronti Multi-Anno</h1>
          <p className="text-[#86868B] mt-1">
            Analisi comparativa dal 2023 ad oggi, per anno e per mese
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Year Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {yearly_summary.map((ys) => (
          <Card 
            key={ys.year}
            className={`bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] cursor-pointer transition-all hover:shadow-lg ${
              selectedYear === ys.year ? 'ring-2 ring-[#007AFF]' : ''
            }`}
            style={{ borderLeft: `4px solid ${getYearColor(ys.year)}` }}
            onClick={() => setSelectedYear(ys.year)}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span 
                  className="text-2xl font-bold"
                  style={{ color: getYearColor(ys.year) }}
                >
                  {ys.year}
                </span>
                <Badge variant="outline" className="text-xs">
                  {ys.services_count} servizi
                </Badge>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#86868B]">Fatturato</span>
                  <div className="text-right">
                    <span className="font-semibold text-[#1D1D1F]">{formatCurrency(ys.revenue)}</span>
                    <VariationBadge value={ys.revenue_var} />
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#86868B]">Costi</span>
                  <div className="text-right">
                    <span className="font-semibold text-[#FF3B30]">{formatCurrency(ys.costs)}</span>
                    <VariationBadge value={ys.costs_var} />
                  </div>
                </div>
                
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="text-sm font-medium text-[#1D1D1F]">Utile</span>
                  <div className="text-right">
                    <span className={`font-bold ${ys.profit >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                      {formatCurrency(ys.profit)}
                    </span>
                    <VariationBadge value={ys.profit_var} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="chart" className="space-y-4">
        <TabsList className="bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="chart" className="rounded-lg data-[state=active]:bg-white">
            <LineChartIcon className="w-4 h-4 mr-2" />
            Grafico Comparativo
          </TabsTrigger>
          <TabsTrigger value="table" className="rounded-lg data-[state=active]:bg-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            Tabella Dettagliata
          </TabsTrigger>
          <TabsTrigger value="monthly" className="rounded-lg data-[state=active]:bg-white">
            <Calendar className="w-4 h-4 mr-2" />
            Dettaglio Mensile
          </TabsTrigger>
        </TabsList>

        {/* Chart Tab */}
        <TabsContent value="chart" className="space-y-4">
          {/* Metric Selector */}
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Andamento Mensile - Tutti gli Anni
              </CardTitle>
              <Select value={selectedMetric} onValueChange={setSelectedMetric}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(metricLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthly_chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v) => selectedMetric === "services_count" ? v : `${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      formatter={(value, name) => {
                        const year = name.split('_')[1];
                        return [
                          selectedMetric === "services_count" ? value : formatCurrency(value),
                          year
                        ];
                      }}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend />
                    {years.map((year) => (
                      <Line
                        key={year}
                        type="monotone"
                        dataKey={`${selectedMetric}_${year}`}
                        name={`${year}`}
                        stroke={getYearColor(year)}
                        strokeWidth={2}
                        dot={{ fill: getYearColor(year), strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend with colors */}
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {years.map((year) => (
                  <div key={year} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getYearColor(year) }}
                    />
                    <span className="text-sm font-medium">{year}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bar Chart Comparison */}
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Confronto Mensile - {metricLabels[selectedMetric]}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly_chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => selectedMetric === "services_count" ? v : `${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      formatter={(value) => selectedMetric === "services_count" ? value : formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend />
                    {years.map((year) => (
                      <Bar
                        key={year}
                        dataKey={`${selectedMetric}_${year}`}
                        name={`${year}`}
                        fill={getYearColor(year)}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Table Tab */}
        <TabsContent value="table">
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Riepilogo Annuale Completo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold">Anno</TableHead>
                      <TableHead className="text-right">Fatturato</TableHead>
                      <TableHead className="text-right">Var.</TableHead>
                      <TableHead className="text-right">Costi</TableHead>
                      <TableHead className="text-right">Var.</TableHead>
                      <TableHead className="text-right">Fatt. Passive</TableHead>
                      <TableHead className="text-right">Utile</TableHead>
                      <TableHead className="text-right">Var.</TableHead>
                      <TableHead className="text-right">Margine</TableHead>
                      <TableHead className="text-right">Servizi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {yearly_summary.map((ys) => (
                      <TableRow key={ys.year} className="hover:bg-gray-50">
                        <TableCell>
                          <span 
                            className="font-bold text-lg"
                            style={{ color: getYearColor(ys.year) }}
                          >
                            {ys.year}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(ys.revenue)}
                        </TableCell>
                        <TableCell className="text-right">
                          <VariationBadge value={ys.revenue_var} />
                        </TableCell>
                        <TableCell className="text-right text-[#FF3B30] font-medium">
                          {formatCurrency(ys.costs)}
                        </TableCell>
                        <TableCell className="text-right">
                          <VariationBadge value={ys.costs_var} />
                        </TableCell>
                        <TableCell className="text-right text-[#FF9500]">
                          {formatCurrency(ys.invoices_payable)}
                        </TableCell>
                        <TableCell className={`text-right font-bold ${ys.profit >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                          {formatCurrency(ys.profit)}
                        </TableCell>
                        <TableCell className="text-right">
                          <VariationBadge value={ys.profit_var} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={ys.margin >= 0 ? "default" : "destructive"}>
                            {ys.margin.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Briefcase className="w-4 h-4 text-[#86868B]" />
                            {ys.services_count}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Additional metrics table */}
          <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mt-4">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Movimenti Cassa e Banca per Anno
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold">Anno</TableHead>
                      <TableHead className="text-right">Entrate Cassa</TableHead>
                      <TableHead className="text-right">Uscite Cassa</TableHead>
                      <TableHead className="text-right">Saldo Cassa</TableHead>
                      <TableHead className="text-right">Entrate Banca</TableHead>
                      <TableHead className="text-right">Uscite Banca</TableHead>
                      <TableHead className="text-right">Saldo Banca</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {yearly_summary.map((ys) => (
                      <TableRow key={ys.year} className="hover:bg-gray-50">
                        <TableCell>
                          <span 
                            className="font-bold text-lg"
                            style={{ color: getYearColor(ys.year) }}
                          >
                            {ys.year}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-[#34C759] font-medium">
                          {formatCurrency(ys.income)}
                        </TableCell>
                        <TableCell className="text-right text-[#FF3B30] font-medium">
                          {formatCurrency(ys.expenses)}
                        </TableCell>
                        <TableCell className={`text-right font-bold ${ys.income - ys.expenses >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                          {formatCurrency(ys.income - ys.expenses)}
                        </TableCell>
                        <TableCell className="text-right text-[#34C759]">
                          {formatCurrency(ys.bank_income)}
                        </TableCell>
                        <TableCell className="text-right text-[#FF3B30]">
                          {formatCurrency(ys.bank_expense)}
                        </TableCell>
                        <TableCell className={`text-right font-bold ${ys.bank_income - ys.bank_expense >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                          {formatCurrency(ys.bank_income - ys.bank_expense)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Detail Tab */}
        <TabsContent value="monthly" className="space-y-4">
          {/* Year Selector */}
          <div className="flex gap-2 flex-wrap">
            {years.map((year) => (
              <Button
                key={year}
                variant={selectedYear === year ? "default" : "outline"}
                onClick={() => setSelectedYear(year)}
                className="gap-2"
                style={selectedYear === year ? { backgroundColor: getYearColor(year) } : {}}
              >
                <Calendar className="w-4 h-4" />
                {year}
              </Button>
            ))}
          </div>

          {selectedYear && monthly_tables[selectedYear] && (
            <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2">
                  <span 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: getYearColor(selectedYear) }}
                  />
                  Dettaglio Mensile {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-semibold">Mese</TableHead>
                        <TableHead className="text-right">Fatturato</TableHead>
                        <TableHead className="text-right">Costi</TableHead>
                        <TableHead className="text-right">Fatt. Passive</TableHead>
                        <TableHead className="text-right">Utile</TableHead>
                        <TableHead className="text-right">Entrate</TableHead>
                        <TableHead className="text-right">Uscite</TableHead>
                        <TableHead className="text-right">Servizi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthly_tables[selectedYear].map((m) => (
                        <TableRow key={m.month} className="hover:bg-gray-50">
                          <TableCell className="font-medium">{m.month_name}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(m.revenue)}
                          </TableCell>
                          <TableCell className="text-right text-[#FF3B30]">
                            {formatCurrency(m.costs)}
                          </TableCell>
                          <TableCell className="text-right text-[#FF9500]">
                            {formatCurrency(m.invoices_payable)}
                          </TableCell>
                          <TableCell className={`text-right font-bold ${m.profit >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                            {formatCurrency(m.profit)}
                          </TableCell>
                          <TableCell className="text-right text-[#34C759]">
                            {formatCurrency(m.income)}
                          </TableCell>
                          <TableCell className="text-right text-[#FF3B30]">
                            {formatCurrency(m.expenses)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline">{m.services_count}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow className="bg-gray-50 font-bold">
                        <TableCell>TOTALE</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(monthly_tables[selectedYear].reduce((s, m) => s + m.revenue, 0))}
                        </TableCell>
                        <TableCell className="text-right text-[#FF3B30]">
                          {formatCurrency(monthly_tables[selectedYear].reduce((s, m) => s + m.costs, 0))}
                        </TableCell>
                        <TableCell className="text-right text-[#FF9500]">
                          {formatCurrency(monthly_tables[selectedYear].reduce((s, m) => s + m.invoices_payable, 0))}
                        </TableCell>
                        <TableCell className={`text-right ${
                          monthly_tables[selectedYear].reduce((s, m) => s + m.profit, 0) >= 0 
                            ? 'text-[#34C759]' : 'text-[#FF3B30]'
                        }`}>
                          {formatCurrency(monthly_tables[selectedYear].reduce((s, m) => s + m.profit, 0))}
                        </TableCell>
                        <TableCell className="text-right text-[#34C759]">
                          {formatCurrency(monthly_tables[selectedYear].reduce((s, m) => s + m.income, 0))}
                        </TableCell>
                        <TableCell className="text-right text-[#FF3B30]">
                          {formatCurrency(monthly_tables[selectedYear].reduce((s, m) => s + m.expenses, 0))}
                        </TableCell>
                        <TableCell className="text-right">
                          {monthly_tables[selectedYear].reduce((s, m) => s + m.services_count, 0)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Monthly Bar Chart for selected year */}
                <div className="mt-6 h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={monthly_tables[selectedYear]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month_name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="revenue" name="Fatturato" fill={getYearColor(selectedYear)} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="costs" name="Costi" fill="#FF3B30" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="profit" name="Utile" stroke="#34C759" strokeWidth={3} dot={{ fill: '#34C759' }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Year vs Year Comparison */}
          {years.length > 1 && (
            <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                  Confronto Mese per Mese - Tutti gli Anni
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-semibold sticky left-0 bg-white">Mese</TableHead>
                        {years.map((year) => (
                          <TableHead 
                            key={year} 
                            className="text-center"
                            colSpan={2}
                          >
                            <span style={{ color: getYearColor(year) }} className="font-bold">
                              {year}
                            </span>
                          </TableHead>
                        ))}
                      </TableRow>
                      <TableRow className="bg-gray-50">
                        <TableHead className="sticky left-0 bg-gray-50"></TableHead>
                        {years.map((year) => (
                          <>
                            <TableHead key={`${year}-rev`} className="text-right text-xs">Fatturato</TableHead>
                            <TableHead key={`${year}-profit`} className="text-right text-xs">Utile</TableHead>
                          </>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"].map((monthName, idx) => (
                        <TableRow key={monthName} className="hover:bg-gray-50">
                          <TableCell className="font-medium sticky left-0 bg-white">{monthName}</TableCell>
                          {years.map((year) => {
                            const monthData = monthly_tables[year]?.[idx + 1];
                            return (
                              <>
                                <TableCell key={`${year}-${monthName}-rev`} className="text-right text-sm">
                                  {formatCurrency(monthData?.revenue || 0)}
                                </TableCell>
                                <TableCell 
                                  key={`${year}-${monthName}-profit`} 
                                  className={`text-right text-sm font-medium ${
                                    (monthData?.profit || 0) >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
                                  }`}
                                >
                                  {formatCurrency(monthData?.profit || 0)}
                                </TableCell>
                              </>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Confronti;
